import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import { Accordion, AccordionTab } from 'primereact/accordion';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { SegmentEventTypes } from '../../data/tracking';
import Analytics from '../../utils/Analytics';

/**
 * Dev note:
 * To correctly use WrappedAccordion component, here's how at the caller side:
 * 1. Import WrappedAccordion and WrappedAccordionTabTemplate component.
 * 2. WrappedAccordionTabTemplate component should contain at least TWO components as such
 *			<WrappedAccordionTabTemplate>
 *				<Component1 />
 * 				<Component2 />
 *   		 </WrappedAccordionTabTemplate>
 *    where the first component will be used for the tab header, and
 *    the rest component(s) will be for the tab content.
 * 3. There can be multiple WrappedAccordionTabTemplate components inside WrappedAccordion.
 *
 * For example,
 *
 * 			<WrappedAccordion
 *				onTabChange={activeIndex => { this.setState({ activeIndex }); }}
 *			>
 *				{sortedServices.slice(0, numberOfServicesToShow).map((item, index) => (
 *					<WrappedAccordionTabTemplate
 *						key={`${item.ServiceType}_${item.PackageType}`}
 *					>
 *						<ServiceHeader
 *							isActive={activeIndex === index}
 *							accordionTabItemDetails={item}
 *							serviceSelectionHandler={this.serviceSelectionHandler}
 *						/>
 *						<ServiceInfo accordionTabItemDetails={item} />
 *					</WrappedAccordionTabTemplate>
 *				))}
 *			</WrappedAccordion>
 */
export const WrappedAccordionTabTemplate = ({ children }) => children;
WrappedAccordionTabTemplate.type = (<WrappedAccordionTabTemplate />).type;

export class WrappedAccordion extends Component {
	constructor(props) {
		super(props);

		this.state = {
			// check if there's a default active tab to expand
			activeIndex:
				isArray(props.activeIndex) || isNumber(props.activeIndex)
					? props.activeIndex
					: null,
		};
	}

	componentDidUpdate(prevProps) {
		const { activeIndex } = this.props;

		// the parent can change the activeIndex to expand the tab
		if (prevProps.activeIndex !== activeIndex) {
			this.updateActiveIndex(activeIndex);
		}
	}

	// update state object
	updateActiveIndex = (activeIndex = null, callback) => {
		this.setState(
			{
				activeIndex,
			},
			callback
		);
	};

	// tabChange handler
	onTabChange = event => {
		const { onTabChange, dataqa } = this.props;
		const activeIndex = event.index;

		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'accordian',
				field_name_dataqa: `ui-accordian-${dataqa}`,
				field_value: activeIndex,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});

		this.updateActiveIndex(activeIndex, () => {
			if (onTabChange) {
				// notify the parent about the new activeIndex value
				onTabChange(activeIndex);
			}
		});
	};

	renderAccordionTabs = () => {
		const { children } = this.props;

		// Here we are using React.Children.map because we don't want to worry
		// about the children's type (array or a function)
		return React.Children.map(children, template => {
			// render only WrappedAccordionTabTemplate component
			if (template.type === WrappedAccordionTabTemplate.type) {
				const { props } = template;

				// The first child component is used for the tab header, and
				// the rest component(s) are for the tab content.
				const header = props.children.slice(0, 1);
				const content = props.children.slice(1);

				return <AccordionTab header={header}>{content}</AccordionTab>;
			}

			return null;
		});
	};

	render() {
		const { className, dataqa, multiple = false } = this.props;
		const { activeIndex } = this.state;

		return (
			<div className="common-ui-accordion">
				<Accordion
					activeIndex={activeIndex}
					className={className}
					data-qa={dataqa}
					dataqa={`ui-accordian-${dataqa}`}
					multiple={multiple}
					onTabChange={this.onTabChange}
				>
					{this.renderAccordionTabs()}
				</Accordion>
			</div>
		);
	}
}
WrappedAccordion.propTypes = {
	// active index or indexes of the element.
	activeIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),

	// style class of the element.
	className: PropTypes.string,

	// For QA automation testing.
	dataqa: PropTypes.string,

	// when enabled, multiple tabs can be activated at the same time.
	multiple: PropTypes.bool,

	// (Optional) Pass the function to get activeIndex value
	onTabChange: PropTypes.func,
};
